import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { getMe, login as loginApi, logout as logoutApi, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await getMe();
      setUser(res.data);
    } catch (error) {
      if (error?.response?.status !== 401) {
        console.error("Auth check failed:", error?.response?.status || error.message);
      }
      localStorage.removeItem("access_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const loginFn = useCallback(async (email, password) => {
    try {
      const res = await loginApi({ email, password });
      const { token, ...userData } = res.data;
      if (token) {
        localStorage.setItem("access_token", token);
      }
      setUser(userData);
      return { success: true };
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      console.error("Login failed:", msg);
      return { success: false, error: msg };
    }
  }, []);

  const logoutFn = useCallback(async () => {
    try {
      await logoutApi();
    } catch (error) {
      console.error("Logout error:", error.message);
    }
    localStorage.removeItem("access_token");
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, loading, login: loginFn, logout: logoutFn }), [user, loading, loginFn, logoutFn]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
